import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/layout/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { PROJECT_STATUS } from "@/lib/discovery";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Plus, Building2, ArrowRight } from "lucide-react";

export default function Projects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); }, []);
  const canCreate = user?.role === "admin" || user?.role === "consultant";

  return (
    <>
      <PageHeader title="Projects" subtitle="All discovery projects across your customers."
        actions={canCreate && (
          <Button data-testid="new-project-btn" onClick={() => navigate("/projects/new")} className="rounded-lg bg-sky-600 hover:bg-sky-700">
            <Plus className="mr-1.5 h-4 w-4" /> New Discovery
          </Button>
        )} />
      <div className="flex-1 px-6 py-8 lg:px-10">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {projects?.map((p) => {
            const st = PROJECT_STATUS[p.status] || { label: p.status, tone: "slate" };
            return (
              <button key={p.id} data-testid={`project-card-${p.id}`} onClick={() => navigate(`/projects/${p.id}`)}
                className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 text-left transition-all duration-200 hover:-translate-y-1 hover:border-sky-300 hover:shadow-md">
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100"><Building2 className="h-5 w-5 text-slate-500" /></div>
                  <StatusBadge label={st.label} tone={st.tone} />
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold tracking-tight text-slate-900">{p.customer_name}</h3>
                <p className="mt-1 text-sm text-slate-500">{p.project_name}</p>
                <div className="mt-4 flex items-center gap-2">
                  <span className="rounded-md bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700">{p.product}</span>
                  <span className="text-xs text-slate-400">{p.country}</span>
                </div>
                <div className="mt-5">
                  <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-500">
                    <span>Discovery Progress</span><span className="text-slate-700">{p.progress}%</span>
                  </div>
                  <Progress value={p.progress} className="h-2" />
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 text-sm">
                  <span className="text-slate-500">{p.consultant_name || "Unassigned"}</span>
                  <span className="flex items-center gap-1 font-semibold text-sky-600 group-hover:gap-2 transition-all">Open <ArrowRight className="h-4 w-4" /></span>
                </div>
              </button>
            );
          })}
          {projects && projects.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
              <p className="font-heading text-lg font-bold text-slate-700">No projects yet</p>
              <p className="mt-1 text-sm text-slate-400">Start a guided discovery for your first customer.</p>
              {canCreate && <Button onClick={() => navigate("/projects/new")} className="mt-4 rounded-lg bg-sky-600 hover:bg-sky-700"><Plus className="mr-1.5 h-4 w-4" /> New Discovery</Button>}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
